import { LOGO_SRC } from "@/config";
import { Instagram, Facebook } from "lucide-react";

const EXPLORE_LINKS = [
  { label: "How It Works", href: "#how-it-works" },
  { label: "Benefits", href: "#benefits" },
  { label: "Explore App", href: "#inside-the-app" },
  { label: "Our Mission", href: "#about" },
];

const LEGAL_LINKS = [
  { label: "Terms of Service", href: "/legal/terms-of-service" },
  { label: "Privacy Policy", href: "/legal/privacy-policy" },
  { label: "Marketplace & Producer Disclaimer", href: "/legal/marketplace-disclaimer" },
  { label: "Community Guidelines", href: "/legal/community-guidelines" },
  { label: "Subscription & Payment Terms", href: "/legal/subscription-terms" },
  { label: "Producer Agreement", href: "/legal/producer-agreement" },
];

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#1C241B] text-[#F9F6F0] py-16 px-4 sm:px-6 lg:px-12 border-t border-[#4A6B41]/30"
    >
      <div className="mx-auto max-w-7xl grid gap-12 md:grid-cols-[1.4fr_1fr_1fr_1.2fr]">
        <div>
          <div className="flex items-center gap-4">
            <img
              src={LOGO_SRC}
              alt="Source2Table logo"
              className="h-20 w-20 object-contain p-0.5"
            />
            <span className="font-sans font-extrabold text-4xl leading-none tracking-tight">
              <span className="text-[#F9F6F0]">Source</span>
              <span className="text-[#4A6B41]">2</span>
              <span className="text-[#F9F6F0]">Table</span>
            </span>
          </div>

          <div className="mt-5 flex items-center gap-3">
            <a
              data-testid="footer-instagram-link"
              href="https://www.instagram.com/source2tablenetwork?psln=aGtscmRhM2l6djFj&utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow Source2Table on Instagram"
              title="Follow Source2Table on Instagram"
              className="inline-flex h-16 w-16 items-center justify-center rounded-2xl text-white shadow-md transition-transform hover:scale-110 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/30"
              style={{
                background:
                  "linear-gradient(45deg,#feda75,#fa7e1e,#d62976,#962fbf,#4f5bd5)",
              }}
            >
              <Instagram size={40} />
            </a>
            <a
              data-testid="footer-facebook-link"
              href="https://www.facebook.com/profile.php?id=61595429817103"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow Source2Table on Facebook"
              title="Follow Source2Table on Facebook"
              className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[#1877F2] text-white shadow-md transition-transform hover:scale-110 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/30"
            >
              <Facebook size={40} />
            </a>
          </div>

          <p className="mt-6 max-w-sm font-sans text-base text-[#F9F6F0]/70 leading-relaxed">
            Connecting local farms directly to your table —
            where fresh starts.
          </p>
        </div>

        {/* Explore */}
        <div>
          <h4 className="font-sans text-sm font-bold uppercase tracking-[0.15em] text-[#F9F6F0]/60">
            Explore
          </h4>
          <ul className="mt-4 space-y-3">
            {EXPLORE_LINKS.map((l) => (
              <li key={l.label}>
                <a
                  data-testid={`footer-link-${l.label.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}
                  href={l.href}
                  className="font-sans text-sm text-[#F9F6F0]/80 hover:text-[#F9F6F0] transition-colors"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Legal — opens each document in a new tab */}
        <div>
          <h4 className="font-sans text-sm font-bold uppercase tracking-[0.15em] text-[#F9F6F0]/60">
            Legal
          </h4>
          <ul className="mt-4 space-y-2.5">
            {LEGAL_LINKS.map((l) => (
              <li key={l.label}>
                <a
                  data-testid={`footer-legal-${l.href.split("/").pop()}`}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-sans text-sm text-[#F9F6F0]/80 hover:text-[#F9F6F0] transition-colors leading-snug"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-sans text-sm font-bold uppercase tracking-[0.15em] text-[#F9F6F0]/60">
            Contact
          </h4>
          <a
            data-testid="footer-support-email"
            href="mailto:support@source2table.com"
            className="mt-3 inline-block font-sans text-sm text-[#F9F6F0]/80 hover:text-[#F9F6F0] transition-colors"
          >
            support@source2table.com
          </a>

          <div className="mt-5 inline-block">
            <img
              data-testid="footer-app-badges"
              src="/app-badges.png"
              alt="Get it on Google Play and Download on the App Store"
              className="w-32 h-auto object-contain invert"
            />
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl mt-12 pt-6 border-t border-[#F9F6F0]/10 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="font-sans text-xs text-[#F9F6F0]/50">
          © {new Date().getFullYear()} Source2Table. All rights reserved.
        </p>
        <p className="font-sans text-sm text-[#F9F6F0]/50">
          licensed 2026
        </p>
      </div>
    </footer>
  );
};
